export interface IPromptConfiguration {
    temperature?: number;
    maxTokens?: number;
    topP?: number;
    _id?: string;
}

export interface ICreatePromptPayload {
    name: string;
    key: string;
    description: string;
    allowedProviders: string[];
    allowedModels: string[];
    systemPrompt: string;
    prompt: string;
    configuration: IPromptConfiguration;
}

export interface IUpdatePromptPayload {
    version: number;
    name?: string;
    systemPrompt?: string;
    prompt?: string;
    description?: string;
    allowedProviders?: string[];
    allowedModels?: string[];
    configuration?: IPromptConfiguration;
}

export interface IPromptListItem {
    _id: string;
    name: string;
    key: string;
    description?: string;
    allowedProviders?: string[];
    allowedModels?: string[];
    systemPrompt?: string;
    prompt?: string;
    configuration?: IPromptConfiguration;
    version?: number;
    publishedVersion?: number | null;
    published?: boolean;
    archived?: boolean;
    hasUnpublishedChanges?: boolean;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    updatedBy?: string;
}

export interface IPrompt extends IPromptListItem {}

export interface IPromptsPage {
    size: number;
    totalElements: number;
    totalPages: number;
    number: number;
    hasNextPage: boolean;
}

export interface IPromptsListResponse {
    success: boolean;
    status: number;
    aggregationId: string;
    data: {
        _links?: Record<string, string>;
        page: IPromptsPage;
        elements: IPromptListItem[];
    };
}

export interface IPromptsListResult {
    elements: IPromptListItem[];
    page: IPromptsPage;
}

export interface IPromptChangedBy {
    id?: string;
    name?: string;
    email?: string;
    username?: string;
}

export interface IPromptFieldChange {
    from?: unknown;
    to?: unknown;
}

export interface IPromptHistoryEntry {
    _id?: string;
    version?: number;
    name?: string;
    key?: string;
    description?: string;
    systemPrompt?: string;
    prompt?: string;
    allowedProviders?: string[];
    allowedModels?: string[];
    configuration?: IPromptConfiguration;
    changeType?: string;
    changeNote?: string;
    changes?: Record<string, unknown>;
    changedBy?: IPromptChangedBy;
    createdAt?: string;
    updatedAt?: string;
    timestamp?: string;
    createdBy?: string;
    updatedBy?: string | { username: string; timestamp: string };
    restoredFromVersion?: number | null;
}

export interface IFlattenedChange {
    path: string;
    from: unknown;
    to: unknown;
}

const isChangeLeaf = (value: unknown): value is IPromptFieldChange =>
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    ('from' in value || 'to' in value);

export const flattenPromptChanges = (
    changes: Record<string, unknown> | undefined,
    prefix = '',
): IFlattenedChange[] => {
    if (!changes) return [];
    const result: IFlattenedChange[] = [];
    Object.entries(changes).forEach(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key;
        if (isChangeLeaf(value)) {
            result.push({
                path,
                from: (value as IPromptFieldChange).from,
                to: (value as IPromptFieldChange).to,
            });
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            result.push(...flattenPromptChanges(value as Record<string, unknown>, path));
        }
    });
    return result;
};

export const resolvePromptChanges = (
    entry: IPromptHistoryEntry | null | undefined,
): Record<string, unknown> | undefined => {
    if (!entry) return undefined;
    if (entry.changes && typeof entry.changes === 'object') {
        return entry.changes as Record<string, unknown>;
    }
    return entry as unknown as Record<string, unknown>;
};

export const formatChangeValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (Array.isArray(value)) return value.join(', ') || '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

export interface IPromptHistoryResult {
    elements: IPromptHistoryEntry[];
    page: IPromptsPage;
}

export const PROVIDER_OPTIONS = ['openai', 'gemini'] as const;

export const MODEL_OPTIONS = [
    'gpt-4o',
    'gpt-4o-mini',
    'gemini-2.5-flash',
    'gemini-2.0-flash',
] as const;

export const getPromptId = (prompt: Pick<IPromptListItem, '_id'>) => prompt._id;

export const getPromptStatusLabel = (prompt: IPromptListItem) => {
    if (prompt.archived) return 'Archived';
    if (prompt.published && prompt.hasUnpublishedChanges) return 'Published (draft changes)';
    if (prompt.published) return 'Published';
    return 'Draft';
};

export const getPromptStatusBadgeClass = (prompt: IPromptListItem) => {
    if (prompt.archived) {
        return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700';
    }
    if (prompt.published && prompt.hasUnpublishedChanges) {
        return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700';
    }
    if (prompt.published) {
        return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700';
    }
    return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
};

export const canShowPublishAction = (prompt: IPromptListItem) =>
    !prompt.archived && (!prompt.published || Boolean(prompt.hasUnpublishedChanges));
